import './StreamingNetworks.scss';
import cn from 'classnames';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { MdAdd, MdEdit, MdSave, MdSettings } from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SearchField } from '@Components/SearchField/SearchField';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { VirtualizedGroupedList } from '@Components/VirtualizedGroupedList/VirtualizedGroupedList';
import { Design, Intent, Size } from '@Components/designConstants';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { AddNetworkModal } from './AddNetworkModal';
import { AttributeSchemaModal } from './AttributeSchemaModal';
import { NetworkAttributesTab } from './NetworkAttributesTab';
import { NetworkContactsTab } from './NetworkContactsTab';
import { NetworkContentDetailsTab } from './NetworkContentDetailsTab';
import { NetworkContentTab } from './NetworkContentTab';
import { NetworkGeneralTab } from './NetworkGeneralTab';
import { NetworkPropertiesTab } from './NetworkPropertiesTab';
import {
  colorOf,
  networkGroupOf,
  shortCodeOf,
  StreamingNetworkRecord,
  WorkspaceTab,
  WorkspaceTabType
} from './streaming-networks-constants';
import {
  buildSearchBlobs,
  collectPlatformOptions,
  matchSearchBlobs
} from './streaming-networks-utils';
import { saveNetwork, useDspOptions, useStreamingNetworks } from './use-streaming-networks';

const TABS: { key: WorkspaceTabType; label: string }[] = [
  { key: WorkspaceTab.GENERAL, label: 'General' },
  { key: WorkspaceTab.CONTACTS, label: 'Contacts' },
  { key: WorkspaceTab.CONTENT, label: 'Content' },
  { key: WorkspaceTab.CONTENT_DETAILS, label: 'Content Details' },
  { key: WorkspaceTab.PROPERTIES, label: 'Properties' },
  { key: WorkspaceTab.ATTRIBUTES, label: 'Attributes' }
];

// Tabs whose edits are part of the single whole-network save (one POST covers all four); Content
// Details and Attributes each persist independently through their own endpoint and own edit state.
const SHARED_DRAFT_TABS: WorkspaceTabType[] = [
  WorkspaceTab.GENERAL,
  WorkspaceTab.CONTACTS,
  WorkspaceTab.CONTENT,
  WorkspaceTab.PROPERTIES
];

export const StreamingNetworks = () => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const networksQuery = useStreamingNetworks();
  const dspQuery = useDspOptions();

  const [selectedShortCode, setSelectedShortCode] = useState<string>();
  const [activeTab, setActiveTab] = useState<WorkspaceTabType>(WorkspaceTab.GENERAL);
  const [query, setQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAttributeSchemaModal, setShowAttributeSchemaModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>();
  const [pendingNavigation, setPendingNavigation] = useState<() => void>();

  // shared draft state for General/Contacts/Content/Properties (see SHARED_DRAFT_TABS above)
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<StreamingNetworkRecord>>({});

  const deferredQuery = useDeferredValue(query);

  const networks = useMemo(() => networksQuery.data ?? [], [networksQuery.data]);
  const dspOptions = useMemo(() => dspQuery.data ?? [], [dspQuery.data]);

  useEffect(() => {
    if (networksQuery.error) {
      setError({
        title: "Couldn't load streaming networks",
        message: `Failed to load data: ${networksQuery.error.message}`,
        reportError: networksQuery.error
      });
    }
  }, [networksQuery.error, setError]);

  const networksByCode = useMemo(() => {
    const map = new Map<string, StreamingNetworkRecord>();
    networks.forEach(network => map.set(shortCodeOf(network), network));
    return map;
  }, [networks]);

  const networkGroups = useMemo(
    () => [...new Set(networks.map(networkGroupOf).filter(group => group !== 'Ungrouped'))].sort(),
    [networks]
  );

  const platformOptions = useMemo(() => collectPlatformOptions(networks), [networks]);

  const searchableNetworks = useMemo(
    () => networks.map(network => ({ network, blobs: buildSearchBlobs(network) })),
    [networks]
  );

  const filteredNetworks = useMemo(
    () =>
      searchableNetworks
        .filter(({ blobs }) => matchSearchBlobs(blobs, deferredQuery))
        .map(({ network }) => network)
        .sort(
          (a, b) =>
            networkGroupOf(a).localeCompare(networkGroupOf(b)) || a.name.localeCompare(b.name)
        ),
    [searchableNetworks, deferredQuery]
  );

  useEffect(() => {
    if (!selectedShortCode && networks.length) {
      setSelectedShortCode(shortCodeOf(networks[0]));
    }
  }, [networks, selectedShortCode]);

  const selectedNetwork = selectedShortCode ? networksByCode.get(selectedShortCode) : undefined;
  const combinedData = useMemo<StreamingNetworkRecord | undefined>(
    () => (selectedNetwork ? { ...selectedNetwork, ...draft } : undefined),
    [selectedNetwork, draft]
  );

  const startEditing = () => {
    setDraft({});
    setEditing(true);
  };
  const discardChanges = () => {
    setDraft({});
    setEditing(false);
  };
  const onFieldChange = <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => setDraft(prev => ({ ...prev, [field]: value }));

  const hasPendingDraft = editing && Object.keys(draft).length > 0;

  const selectNetwork = (shortCode: string) => {
    discardChanges();
    setSelectedShortCode(shortCode);
    setActiveTab(WorkspaceTab.GENERAL);
  };

  const handleSelect = (shortCode: string) => {
    if (hasPendingDraft) {
      setPendingNavigation(() => () => selectNetwork(shortCode));
      return;
    }
    selectNetwork(shortCode);
  };

  const trackTabChange = (tab: WorkspaceTabType) =>
    Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.STREAMING_NETWORKS, option_selected: tab });

  // Content Details and Attributes read persisted data, not the shared draft (see
  // `persistedNetwork` below) — leaving a shared-draft tab with pending edits to visit one of them
  // would otherwise let the user act on data that was never saved. Confirm before discarding.
  const changeTab = (tab: WorkspaceTabType) => {
    discardChanges();
    setActiveTab(tab);
    trackTabChange(tab);
  };

  const handleTabChange = (tab: WorkspaceTabType) => {
    if (
      hasPendingDraft &&
      SHARED_DRAFT_TABS.includes(activeTab) &&
      !SHARED_DRAFT_TABS.includes(tab)
    ) {
      setPendingNavigation(() => () => changeTab(tab));
      return;
    }
    setActiveTab(tab);
    trackTabChange(tab);
  };

  const confirmNavigation = () => {
    pendingNavigation?.();
    setPendingNavigation(undefined);
  };

  const handleSave = async () => {
    if (!combinedData) return;

    for (const property of combinedData.properties) {
      if (property.isNew && property.propertyShortCode.length > 10) {
        try {
          await setAreYouSure({
            title: 'Long Short Code',
            message: `The short code "${property.propertyShortCode}" is very long. You should consider using an abbreviation or some other way of shortening it (unless any other name would be overly confusing). Are you sure you want to create this sub-property with this short code?`,
            okayText: 'Save anyway',
            cancelText: 'Change the short code'
          });
        } catch {
          return;
        }
      }
    }

    setSaving(true);
    try {
      await saveNetwork(combinedData);
      await networksQuery.refetch();
      discardChanges();
      setToastMessage('Changes saved');
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.STREAMING_NETWORKS, entity: 'network' });
    } catch (e) {
      setError({ message: `Save failed: ${e.message}`, reportError: e });
    }
    setSaving(false);
  };

  const handleCreated = (shortCode: string) => {
    setShowAddModal(false);
    setSelectedShortCode(shortCode);
    setActiveTab(WorkspaceTab.GENERAL);
    setToastMessage('Network added');
    networksQuery.refetch();
  };

  const { isLoading } = networksQuery;

  return (
    <Page
      title={PAGE_NAMES.STREAMING_NETWORKS}
      pageType={PAGE_NAMES.STREAMING_NETWORKS}
      minHeight='640px'
      isLoading={isLoading}
      actions={
        <div className='sn-actions'>
          <Button
            size={Size.LG}
            design={Design.NEUTRAL}
            leadingElement={<MdSettings />}
            onClick={() => setShowAttributeSchemaModal(true)}
          >
            Properties
          </Button>
          <Button
            size={Size.LG}
            leadingElement={<MdAdd />}
            onClick={() => {
              setShowAddModal(true);
              Mixpanel.track(MxE.ModalOpened, {
                page: PAGE_NAMES.STREAMING_NETWORKS,
                modal: 'add_network'
              });
            }}
          >
            Add network
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <FullPageSpinner />
      ) : (
        <div className={cn('streaming-networks', { saving })}>
          <NetworkList
            networks={filteredNetworks}
            total={networks.length}
            selectedShortCode={selectedShortCode}
            query={query}
            onQueryChange={setQuery}
            onSelect={handleSelect}
          />
          <DetailPanel
            network={combinedData}
            persistedNetwork={selectedNetwork}
            activeTab={activeTab}
            editing={editing}
            saving={saving}
            dspOptions={dspOptions}
            platformOptions={platformOptions}
            networkGroups={networkGroups}
            onTabChange={handleTabChange}
            onFieldChange={onFieldChange}
            onStartEditing={startEditing}
            onDiscardChanges={discardChanges}
            onSave={handleSave}
            onRefetch={networksQuery.refetch}
            onRefetchAttributes={networksQuery.refetchOld}
          />
        </div>
      )}
      <AddNetworkModal
        show={showAddModal}
        existingShortCodes={networks.map(shortCodeOf)}
        existingGroups={networkGroups}
        platformOptions={platformOptions}
        onHide={() => setShowAddModal(false)}
        onCreated={handleCreated}
      />
      <AttributeSchemaModal
        show={showAttributeSchemaModal}
        onHide={() => setShowAttributeSchemaModal(false)}
      />
      {pendingNavigation && (
        <AlertDialog
          show
          onHide={() => setPendingNavigation(undefined)}
          intent={Intent.WARNING}
          title='Discard unsaved changes?'
          body='Leaving will discard your unsaved changes to this network.'
          primaryAction={{ onClick: confirmNavigation, label: 'Discard changes' }}
        />
      )}
      <ToastContainer
        position='top-center'
        containerPosition='fixed'
        className='sn-toast-container'
      >
        <Toast
          className='sn-toast'
          show={Boolean(toastMessage)}
          onClose={() => setToastMessage(undefined)}
          delay={2500}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Page>
  );
};

// ---- master list ----

interface NetworkListProps {
  networks: StreamingNetworkRecord[];
  total: number;
  selectedShortCode?: string;
  query: string;
  onQueryChange: (value: string) => void;
  onSelect: (shortCode: string) => void;
}

const NetworkList = ({
  networks,
  total,
  selectedShortCode,
  query,
  onQueryChange,
  onSelect
}: NetworkListProps) => (
  <div className='sn-master'>
    <div className='sn-master-head'>
      <div className='sn-master-title'>
        <h2>Networks</h2>
        <span className='sn-count'>{total}</span>
      </div>
      <SearchField
        size={Size.LG}
        value={query}
        placeholder='Search networks, contacts, content…'
        onChange={event => onQueryChange(event.target.value)}
      />
    </div>
    <div className='sn-netlist'>
      <VirtualizedGroupedList
        items={networks}
        groupOf={networkGroupOf}
        keyOf={shortCodeOf}
        itemHeight={56}
        groupLabelClassName='sn-group-label'
        groupCountClassName='sn-group-count'
        emptyClassName='sn-empty'
        emptyMessage='No networks match.'
        renderItem={network => (
          <NetworkListItem
            network={network}
            selected={shortCodeOf(network) === selectedShortCode}
            onSelect={onSelect}
          />
        )}
      />
    </div>
  </div>
);

interface NetworkListItemProps {
  network: StreamingNetworkRecord;
  selected: boolean;
  onSelect: (shortCode: string) => void;
}

const NetworkListItem = ({ network, selected, onSelect }: NetworkListItemProps) => (
  <InteractiveDiv
    className={cn('sn-net-item', { active: selected })}
    onClick={() => onSelect(shortCodeOf(network))}
  >
    <span className='sn-swatch' style={{ backgroundColor: colorOf(network) }} />
    <EntityLogo kind={EntityKind.NETWORK} id={shortCodeOf(network)} size={Size.LG} />
    <span className='sn-net-main'>
      <span className='sn-net-name'>{network.name || shortCodeOf(network)}</span>
      <span className='sn-net-sub'>{shortCodeOf(network)}</span>
    </span>
  </InteractiveDiv>
);

// ---- detail panel ----

interface DetailPanelProps {
  network?: StreamingNetworkRecord;
  // The persisted (un-drafted) network — Content Details and Attributes save independently of the
  // shared draft (see SHARED_DRAFT_TABS) and must not read or act on unsaved shared-tab edits.
  persistedNetwork?: StreamingNetworkRecord;
  activeTab: WorkspaceTabType;
  editing: boolean;
  saving: boolean;
  dspOptions: string[];
  platformOptions: string[];
  networkGroups: string[];
  onTabChange: (tab: WorkspaceTabType) => void;
  onFieldChange: <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => void;
  onStartEditing: () => void;
  onDiscardChanges: () => void;
  onSave: () => Promise<void>;
  onRefetch: () => Promise<unknown>;
  onRefetchAttributes: () => Promise<unknown>;
}

const DetailPanel = ({
  network,
  persistedNetwork,
  activeTab,
  editing,
  saving,
  dspOptions,
  platformOptions,
  networkGroups,
  onTabChange,
  onFieldChange,
  onStartEditing,
  onDiscardChanges,
  onSave,
  onRefetch,
  onRefetchAttributes
}: DetailPanelProps) => {
  if (!network || !persistedNetwork) {
    return <div className='sn-detail sn-empty'>Select a network to see its metadata.</div>;
  }

  const showSharedEditControls = SHARED_DRAFT_TABS.includes(activeTab);

  return (
    <div className='sn-detail'>
      <div className='sn-detail-head'>
        <span className='sn-detail-swatch' style={{ backgroundColor: colorOf(network) }} />
        <div className='sn-detail-title'>
          <h3>{network.name || shortCodeOf(network)}</h3>
          <span className='sn-pill mono'>{shortCodeOf(network)}</span>
        </div>
        {showSharedEditControls && (
          <div className='sn-detail-actions'>
            {editing ? (
              <>
                <Button
                  size={Size.LG}
                  design={Design.NEUTRAL}
                  disabled={saving}
                  onClick={onDiscardChanges}
                >
                  Cancel
                </Button>
                <Button
                  size={Size.LG}
                  loading={saving}
                  leadingElement={<MdSave />}
                  onClick={onSave}
                >
                  Save changes
                </Button>
              </>
            ) : (
              <Button
                size={Size.LG}
                design={Design.NEUTRAL}
                leadingElement={<MdEdit />}
                onClick={onStartEditing}
              >
                Edit
              </Button>
            )}
          </div>
        )}
      </div>
      <div className='sn-tabs'>
        {TABS.map(tab => (
          <InteractiveDiv
            key={tab.key}
            className={cn('sn-tab', { active: activeTab === tab.key })}
            onClick={() => onTabChange(tab.key)}
          >
            {tab.label}
          </InteractiveDiv>
        ))}
      </div>
      <div className='sn-detail-body'>
        {activeTab === WorkspaceTab.GENERAL && (
          <NetworkGeneralTab
            network={network}
            editing={editing}
            dspOptions={dspOptions}
            platformOptions={platformOptions}
            networkGroups={networkGroups}
            onFieldChange={onFieldChange}
          />
        )}
        {activeTab === WorkspaceTab.CONTACTS && (
          <NetworkContactsTab network={network} editing={editing} onFieldChange={onFieldChange} />
        )}
        {activeTab === WorkspaceTab.CONTENT && (
          <NetworkContentTab network={network} editing={editing} onFieldChange={onFieldChange} />
        )}
        {activeTab === WorkspaceTab.CONTENT_DETAILS && (
          <NetworkContentDetailsTab network={persistedNetwork} onRefetch={onRefetch} />
        )}
        {activeTab === WorkspaceTab.PROPERTIES && (
          <NetworkPropertiesTab network={network} editing={editing} onFieldChange={onFieldChange} />
        )}
        {activeTab === WorkspaceTab.ATTRIBUTES && (
          <NetworkAttributesTab network={persistedNetwork} onRefetch={onRefetchAttributes} />
        )}
      </div>
    </div>
  );
};
